import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, Eye, EyeOff, Users, Globe } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlowUploadPage({ user }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const ALLOWED_FORMATS = ['video/mp4', 'video/webm', 'video/quicktime'];
  
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    
    // Check for allowed formats
    if (!ALLOWED_FORMATS.includes(file.type)) {
      toast.error('Please use MP4 or WebM format for best compatibility');
    }
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }
    setVideo(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!video) { toast.error('Select a video'); return; }
    setUploading(true);
    setProgress(0);

    try {
      const token = localStorage.getItem('dharmbook_token');
      const formData = new FormData();
      formData.append('video', video);
      formData.append('caption', caption);
      formData.append('visibility', visibility);

      await axios.post(`${API}/flow/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      toast.success('Pravah published!');
      navigate('/flow');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const visibilityOpts = [
    { value: 'public', label: 'Everyone', icon: Globe, desc: 'All devotees can see' },
    { value: 'devotees', label: 'Devotees Only', icon: Users, desc: 'Only your devotees' },
    { value: 'only_me', label: 'Only Me', icon: EyeOff, desc: 'Private draft' },
  ];

  return (
    <div className="max-w-lg mx-auto px-4 py-6 pb-24 md:pb-8" data-testid="flow-upload-page">
      <h1
        className="text-2xl font-bold text-gray-900 mb-1"
        style={{ fontFamily: 'Merriweather, serif' }}
      >
        Create Pravah
      </h1>
      <p className="text-sm text-gray-500 mb-6">Share a short spiritual video (up to 60s)</p>

      {/* Video Upload Area */}
      {!preview ? (
        <button
          onClick={() => fileRef.current?.click()}
          className="w-full aspect-[9/16] max-h-[420px] border-2 border-dashed border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-3 bg-gray-50 hover:bg-gray-100 hover:border-[#FF9933] transition-all cursor-pointer group"
          data-testid="flow-upload-area"
        >
          <div className="w-14 h-14 rounded-full bg-[#FF9933]/10 flex items-center justify-center group-hover:bg-[#FF9933]/20 transition-colors">
            <Upload className="w-6 h-6 text-[#FF9933]" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700">Tap to select video</p>
            <p className="text-xs text-gray-400 mt-0.5">MP4, MOV, WebM - Max 50MB</p>
          </div>
        </button>
      ) : (
        <div className="relative w-full aspect-[9/16] max-h-[420px] rounded-2xl overflow-hidden bg-black">
          <video
            src={preview}
            className="w-full h-full object-contain"
            controls
            playsInline
          />
          <button
            onClick={() => { setVideo(null); setPreview(null); }}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
        className="hidden"
        onChange={handleFile}
      />

      {/* Caption */}
      <div className="mt-5">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Caption</label>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 500))}
          placeholder="Share wisdom, describe your Pravah..."
          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#FF9933]/40 focus:border-[#FF9933]"
          rows={3}
          data-testid="flow-caption-input"
        />
        <p className="text-xs text-gray-400 text-right mt-1">{caption.length}/500</p>
      </div>

      {/* Visibility */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Visibility</label>
        <div className="space-y-2">
          {visibilityOpts.map(opt => (
            <button
              key={opt.value}
              onClick={() => setVisibility(opt.value)}
              data-testid={`visibility-${opt.value}`}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                visibility === opt.value
                  ? 'border-[#FF9933] bg-[#FF9933]/5'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
            >
              <opt.icon className={`w-5 h-5 flex-shrink-0 ${visibility === opt.value ? 'text-[#FF9933]' : 'text-gray-400'}`} />
              <div>
                <p className={`text-sm font-medium ${visibility === opt.value ? 'text-[#FF9933]' : 'text-gray-700'}`}>{opt.label}</p>
                <p className="text-xs text-gray-400">{opt.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Upload Progress */}
      {uploading && (
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Uploading...</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-[#FF9933] rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {/* Publish Button */}
      <button
        onClick={handleUpload}
        disabled={!video || uploading}
        className="w-full mt-5 py-3 bg-[#FF9933] hover:bg-[#E68A00] disabled:bg-gray-300 text-white font-medium rounded-xl transition-colors text-sm"
        data-testid="flow-publish-btn"
      >
        {uploading ? 'Publishing...' : 'Publish Pravah'}
      </button>
    </div>
  );
}
